/**
 * PGRiskCard — renders get_personal_guarantee_risk (T-38). Registered as
 * `get_personal_guarantee_risk`. Returns null when has_pg !== true (no PG →
 * prose carries). Severity copy is council-approved server-side; this card
 * only maps handoff-severity → CovenantBadge tier and lays out the rows.
 *
 * Adverse-only (founder direction 2026-07-05): benign / false / null / LOW
 * (insolvency_only) dimensions produce NO row — only adverse signals render.
 * "A card renders OR prose carries, never both." (Banker/CPO edge gate.)
 */
import { Shield } from 'react-feather';

import { FinancialNumber } from '../../../components/ui';
import { CovenantBadge } from '../../../components/analysis/CovenantBadge/CovenantBadge';
import type { CovenantTier } from '../../../lib/covenantSeverity';
import type { ToolResultProps } from '../lib/component-registry';

interface PGDimension {
  severity: 'low' | 'medium' | 'high' | 'critical';
  label: string;
  description: string;
}
interface PGEnforcement extends PGDimension {
  value: string;
}
interface PGRiskResult {
  has_pg?: boolean;
  joint_and_several?: PGDimension | null;
  explicitly_unlimited?: PGDimension | null;
  enforcement_trigger?: PGEnforcement | null;
  guarantee_amount?: number | null;
  provider?: string | null;
}

// Backend severity vocab → CovenantBadge tier. Mirrors the CollateralRows
// visual mapping so the chat card and the analysis page read identically.
// `low` → `routine` → CovenantBadge renders null (row is suppressed upstream).
const TIER: Record<PGDimension['severity'], CovenantTier> = {
  critical: 'critical',
  high: 'critical',
  medium: 'high',
  low: 'routine',
};

function RiskRow({ dim }: { dim: PGDimension }) {
  return (
    <div className="px-4 py-2.5">
      <CovenantBadge tier={TIER[dim.severity]} label={dim.label} blurb={dim.description} />
      <p className="mt-1.5 text-body-sm text-txt-secondary">{dim.description}</p>
    </div>
  );
}

export function PGRiskCard({ result }: ToolResultProps) {
  const r = (result ?? {}) as PGRiskResult;
  if (r.has_pg !== true) {
    return null; // no PG detected — prose carries
  }

  const rows = [r.joint_and_several, r.explicitly_unlimited, r.enforcement_trigger].filter(
    (d): d is PGDimension => d != null && TIER[d.severity] !== 'routine',
  );

  return (
    <div className="animate-card-enter mt-2 border border-border bg-surface-card">
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <span className="shrink-0 text-orange">
          <Shield size={16} />
        </span>
        <p className="text-caption font-semibold uppercase tracking-wide text-txt-secondary">
          Personal guarantee risk
        </p>
      </div>

      {(r.provider != null || r.guarantee_amount != null) && (
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <span className="text-body-sm text-txt-secondary">{r.provider ?? 'Guarantor'}</span>
          {r.guarantee_amount != null && (
            <span className="font-mono text-body-sm tabular-nums text-txt-primary">
              <FinancialNumber value={r.guarantee_amount} format="currency" />
            </span>
          )}
        </div>
      )}

      {rows.length > 0 ? (
        <div className="divide-y divide-border">
          {rows.map((dim) => (
            <RiskRow key={dim.label} dim={dim} />
          ))}
        </div>
      ) : (
        <p className="px-4 py-2.5 text-body-sm text-txt-secondary">
          Personal guarantee required. No adverse liability terms detected in the extraction.
        </p>
      )}
    </div>
  );
}
